















































import { Component, Vue, Prop, PropSync } from "vue-property-decorator";
import { DateTime } from "luxon";
import { ContingentRowIdentifier } from "../contingent/ContingentTypes";

export type Pos = {
  x: number;
  y: number;
};

export type SelectedEvent = {
  from: DateTime;
  to: DateTime;
  start: Pos;
  end: Pos;
  topLeft: Pos;
  bottomRight: Pos;
};

@Component
export default class BoxDayTrack extends Vue {
  @Prop() selectable?: boolean;
  @PropSync("selection") selected?: [DateTime, DateTime] | null;
  @Prop({ default: 0 }) startIdx!: number;
  @Prop({ required: true }) from?: DateTime;
  @Prop({ required: true }) to?: DateTime;
  @Prop({ required: true }) cell!: { w: number; h: number };
  @Prop() isMobile?: boolean;
  @Prop() isContingent?: boolean;
  @Prop() identifier?: ContingentRowIdentifier;
  @PropSync("mobileSelection") mobileSelected?: DateTime | null;
  @Prop() highlightPeriod?: string[];

  @Prop({ default: false }) openTimelineOnClick?: boolean;

  @Prop({ default: false }) isForReservations?: boolean;

  get days(): DateTime[] {
    if (!this.from || !this.to) return [];
    const f = this.from;
    return Array.from(
      Array(Math.ceil(this.to.diff(this.from, "days").days)),
      (_, i) => f.plus({ days: i })
    );
  }

  get emptySlots() {
    if (
      this.from &&
      this.to &&
      (!this.isContingent || this.to?.diff(this.from, "days").days <= 7)
    ) {
      return 0;
    }
    return 31 - this.days.length;
  }

  toAssignedMonth(date: DateTime) {
    return DateTime.local(date.year, this.identifier?.month, date.day);
  }

  get formatedSelectedArray() {
    if (
      this.isContingent &&
      this.identifier &&
      this.identifier.month != 0 &&
      this.selected
    ) {
      const fromFormatted = this.toAssignedMonth(this.selected[0]);
      const toFormatted = this.toAssignedMonth(this.selected[1]);
      return [fromFormatted, toFormatted];
    }

    return this.selected;
  }

  dayClass(day: DateTime) {
    const today = new Date();
    const isSelected = this.isSelected(day);
    const firstDay = day.day == 1;
    const isToday =
      day.day == today.getDate() &&
      day.month == today.getMonth() + 1 &&
      day.year == today.getFullYear();

    let highlight = false;
    if (this.highlightPeriod) {
      const highlightStart = DateTime.fromISO(this.highlightPeriod[0]);
      const highlightEnd = DateTime.fromISO(this.highlightPeriod[1]);
      highlight = day >= highlightStart && day <= highlightEnd;
    }

    return {
      firstDay: firstDay && !isSelected && !this.isContingent && !isToday,
      weekend:
        (day.weekday == 6 || day.weekday == 7) &&
        !isSelected &&
        !this.isContingent &&
        !isToday &&
        !firstDay,
      today: isToday && !this.isContingent,
      selected: isSelected,
      highlight: highlight,
    };
  }

  isSelected(day: DateTime) {
    return (
      this.formatedSelectedArray &&
      day >= this.formatedSelectedArray[0] &&
      day <= this.formatedSelectedArray[1]
    );
  }

  selectedMobile(day: DateTime) {
    return {
      selected: day.day === this.mobileSelected?.day,
    };
  }

  getMouseEventOffsetX(event: MouseEvent) {
    if (this.isForReservations) return event.offsetX;

    if (event.currentTarget) {
      return (
        event.clientX -
        (event.currentTarget as HTMLElement).getBoundingClientRect().left
      );
    }

    return 0;
  }

  getMouseEventOffsetY(event: MouseEvent) {
    if (this.isForReservations) return event.offsetY;

    if (event.currentTarget) {
      return (
        event.clientY -
        (event.currentTarget as HTMLElement).getBoundingClientRect().top
      );
    }

    return 0;
  }

  onDblClick(event: MouseEvent) {
    if (!this.selectable && !this.openTimelineOnClick) return;
    if (!this.from) return;

    const day = this.from.plus({
      days:
        Math.floor(this.getMouseEventOffsetX(event) / this.cell.w) -
        this.startIdx,
    });

    const tlx = event.pageX - (this.getMouseEventOffsetX(event) % this.cell.w);
    const tly = event.pageY - this.getMouseEventOffsetY(event);

    if (this.openTimelineOnClick) {
      this.$emit("openTimeline", day.toISODate());
    } else {
      this.$emit("single", {
        from: day,
        to: day,
        start: { x: event.pageX, y: event.pageY },
        end: { x: event.pageX, y: event.pageY },
        topLeft: { x: tlx, y: tly },
        bottomRight: { x: tlx + this.cell.w, y: tly + this.cell.h },
      } as SelectedEvent);
    }
  }

  clicked = false;
  firstClick: DateTime | null = null;

  onClick(event: MouseEvent) {
    if (this.openTimelineOnClick) {
      this.onDblClick(event);
      return;
    }

    if (!this.isMobile) {
      return;
    }

    if (!this.from) return;
    const day = this.from.plus({
      days:
        Math.floor(this.getMouseEventOffsetX(event) / this.cell.w) -
        this.startIdx,
    });

    const tlx = event.pageX - (this.getMouseEventOffsetX(event) % this.cell.w);
    const tly = event.pageY - this.getMouseEventOffsetY(event);

    if (!this.isMobile) {
      if (this.isContingent) {
        if (this.clicked && this.firstClick?.toISODate() == day.toISODate()) {
          this.clicked = false;
          this.firstClick = null;
          this.$emit("select", {
            day: day,
            topLeft: { x: tlx, y: tly },
            bottomRight: { x: tlx + this.cell.w, y: tly + this.cell.h },
          });
          return;
        }
        this.clicked = true;
        this.firstClick = day;
        this.$emit("select", {
          day: day,
          topLeft: { x: tlx, y: tly },
          bottomRight: { x: tlx + this.cell.w, y: tly + this.cell.h },
        });
      }
      return;
    }

    this.mobileSelected = day;

    this.onDblClick(event);
  }

  mouseDown = false;
  startOffset: Pos | null = null;
  startPoint: Pos | null = null;
  endPoint: Pos | null = null;

  onMouseDown(event: MouseEvent) {
    if (this.openTimelineOnClick) {
      return;
    }

    if (!this.selectable) return;
    // Ignore right clicks
    if (event.button === 2) return;

    this.mouseDown = true;
    this.startOffset = {
      x: this.getMouseEventOffsetX(event),
      y: this.getMouseEventOffsetY(event),
    };
    this.startPoint = {
      x: event.pageX,
      y: event.pageY,
    };
    this.endPoint = {
      x: event.pageX,
      y: event.pageY,
    };

    this.selected = this.calcSelection();

    // Start listening for mouse move and up events
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("mouseup", this.onMouseUp);
  }
  onMouseMove(event: MouseEvent) {
    // Update the end point position
    if (this.mouseDown) {
      this.endPoint = {
        x: event.pageX,
        y: event.pageY,
      };

      this.selected = this.calcSelection();
    }
  }
  onMouseUp() {
    // Clean up event listeners
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("mouseup", this.onMouseUp);

    const selected = this.calcSelection();
    if (selected) {
      if (this.startPoint && this.endPoint && this.startOffset && this.from) {
        const [start, end] =
          this.startPoint.x < this.endPoint.x
            ? [this.startPoint, this.endPoint]
            : [this.endPoint, this.startPoint];

        const topLeft = {
          x: this.startPoint.x - (this.startOffset.x % this.cell.w),
          y: this.startPoint.y - this.startOffset.y,
        };
        if (this.startPoint.x > this.endPoint.x) {
          topLeft.x =
            topLeft.x -
            selected[1].diff(selected[0], "days").days * this.cell.w;
        }

        const bottomRight = {
          x:
            topLeft.x +
            (selected[1].diff(selected[0], "days").days + 1) * this.cell.w,
          y: topLeft.y + this.cell.h,
        };

        this.$emit("selected", {
          from: selected[0],
          to: selected[1],
          start,
          end,
          topLeft,
          bottomRight,
        } as SelectedEvent);
      }
    }

    // Reset state
    this.mouseDown = false;
    this.startOffset = null;
    this.startPoint = null;
    this.endPoint = null;
  }

  calcSelection(): [DateTime, DateTime] | null {
    if (!this.startPoint || !this.endPoint || !this.startOffset || !this.from) {
      return null;
    }

    const [top, bottom] = [
      this.startPoint.y - this.startOffset.y,
      this.startPoint.y - this.startOffset.y + this.cell.h,
    ];

    if (this.endPoint.y < top || this.endPoint.y > bottom) {
      return null;
    }

    const startDay = this.from.plus({
      days: Math.floor(this.startOffset.x / this.cell.w) - this.startIdx,
    });

    const endDay = this.from.plus({
      days:
        Math.floor(
          (this.startOffset.x + this.endPoint.x - this.startPoint.x) /
            this.cell.w
        ) - this.startIdx,
    });

    if (startDay.toISODate() === endDay.toISODate()) return null;

    return startDay < endDay ? [startDay, endDay] : [endDay, startDay];
  }

  beforeDestroy() {
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("mouseup", this.onMouseUp);
  }
}
